/** Stable error codes shared by SQL functions, Edge Functions and the UI. */
export const ERROR_CODES = [
  'slot_taken',
  'invalid_slot',
  'idempotency_conflict',
  'not_found',
  'forbidden',
  'cutoff_passed',
  'invalid_state',
  'tenant_unavailable',
  'invalid_input',
  'unauthorized',
  'rate_limited',
  'payload_too_large',
  'unsupported_media',
  'ai_unavailable',
  'telegram_unavailable',
  'internal',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

const STATUS: Record<ErrorCode, number> = {
  slot_taken: 409,
  invalid_slot: 422,
  idempotency_conflict: 409,
  not_found: 404,
  forbidden: 403,
  cutoff_passed: 409,
  invalid_state: 409,
  tenant_unavailable: 404,
  invalid_input: 400,
  unauthorized: 401,
  rate_limited: 429,
  payload_too_large: 413,
  unsupported_media: 415,
  ai_unavailable: 503,
  telegram_unavailable: 503,
  internal: 500,
};

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;
  constructor(code: ErrorCode, message?: string, details?: unknown) {
    super(message ?? code);
    this.code = code;
    this.status = STATUS[code];
    this.details = details;
  }
}

/** Maps a thrown value (Postgres error with our message codes, zod error…) to ApiError. */
export function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  const msg = (e as { message?: string })?.message ?? '';
  if ((ERROR_CODES as readonly string[]).includes(msg)) return new ApiError(msg as ErrorCode);
  const pgCode = (e as { code?: string })?.code;
  if (pgCode === '22P02' || pgCode === '22007' || pgCode === '22008' || pgCode === '23514') return new ApiError('invalid_input');
  if (pgCode === '42501') return new ApiError('forbidden');
  if ((e as { name?: string })?.name === 'ZodError') return new ApiError('invalid_input', 'invalid_input', (e as { issues?: unknown }).issues);
  return new ApiError('internal');
}

/** Russian user-facing text for each code; the UI imports this too. */
export const ERROR_TEXT: Record<ErrorCode, string> = {
  slot_taken: 'Это время только что заняли. Выберите другое.',
  invalid_slot: 'На это время записаться нельзя. Выберите другое.',
  idempotency_conflict: 'Запрос уже обрабатывается. Обновите страницу.',
  not_found: 'Запись не найдена.',
  forbidden: 'Нет доступа.',
  cutoff_passed: 'Изменить запись онлайн уже нельзя — позвоните в студию.',
  invalid_state: 'Это действие недоступно для текущего статуса записи.',
  tenant_unavailable: 'Студия не найдена или временно недоступна.',
  invalid_input: 'Проверьте введённые данные.',
  unauthorized: 'Войдите в кабинет.',
  rate_limited: 'Слишком много запросов. Подождите минуту.',
  payload_too_large: 'Файл слишком большой.',
  unsupported_media: 'Этот формат файла не поддерживается.',
  ai_unavailable: 'Помощник временно недоступен. Записаться можно как обычно.',
  telegram_unavailable: 'Telegram сейчас недоступен. Попробуйте чуть позже.',
  internal: 'Что-то пошло не так. Попробуйте ещё раз.',
};
